import { motion } from 'framer-motion';

const EASE = [0.22, 0.61, 0.36, 1] as const;

interface RevealPhotosProps {
  photoUrls?: string[];
  visible: boolean;
  /** Delay before photos start appearing */
  delay?: number;
  /** Border/shadow color theme */
  borderColor?: string;
  shadowColor?: string;
}

/**
 * Shared photo display for full-canvas reveal scenes.
 * Shows attached photos with a staggered fade-in during the text phase.
 */
export function RevealPhotos({
  photoUrls,
  visible,
  delay = 0.5,
  borderColor = 'rgba(255,255,255,0.6)',
  shadowColor = 'rgba(0,0,0,0.2)',
}: RevealPhotosProps) {
  if (!photoUrls || photoUrls.length === 0) return null;

  return (
    <motion.div
      className="flex justify-center gap-2 sm:gap-3 mb-6 sm:mb-10 flex-wrap px-2 sm:px-4"
      initial={{ opacity: 0, y: 16 }}
      animate={visible ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 1.0, ease: EASE as any }}
    >
      {photoUrls.slice(0, 3).map((url, i) => (
        <motion.div
          key={i}
          className="relative w-24 h-32 sm:w-36 sm:h-44 md:w-44 md:h-56 rounded-lg overflow-hidden"
          style={{
            border: `2px solid ${borderColor}`,
            boxShadow: `0 8px 24px ${shadowColor}`,
            background: 'rgba(0,0,0,0.35)',
          }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={visible ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: delay + 0.2 + i * 0.15, duration: 0.8, ease: EASE as any }}
        >
          {/* Blurred backdrop fills the frame so portrait/landscape never crop faces */}
          <img
            src={url}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-60"
            loading="eager"
            crossOrigin="anonymous"
          />
          <img
            src={url}
            alt=""
            className="relative w-full h-full object-contain"
            loading="eager"
            crossOrigin="anonymous"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              const decoded = decodeURIComponent(url);
              if (target.src !== decoded) target.src = decoded;
            }}
          />
        </motion.div>
      ))}
    </motion.div>
  );
}
